import React, { useEffect, useRef, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, getToken, clearToken, assetUrl } from "../lib/api";
import { Send, AlertTriangle, ArrowLeft, Search, Hash, MapPin, LogIn, LogOut, MessagesSquare, Sparkles, MessageCircle, Bell, BellOff } from "lucide-react";
import { useDmNotifications, setNotificationsEnabled, getNotificationsEnabled } from "../lib/useDmNotifications";
import { processSlashCommand, suggestSlashCommands } from "../lib/slashCommands";
import { toast } from "sonner";

// ─── Utils ──────────────────────────────────────────────────────────
const fmtTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso.replace(" ", "T") + (iso.includes("T") ? "" : "Z"));
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay
    ? d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
};

const Avatar = ({ name, url, size = 36 }) => {
  const initials = (name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  return url ? (
    <img src={assetUrl(url)} alt={name} className="rounded-full object-cover shrink-0 border border-white/10" style={{ width: size, height: size }} />
  ) : (
    <div className="rounded-full shrink-0 bg-yellow-400 text-black font-black flex items-center justify-center" style={{ width: size, height: size, fontSize: size * 0.4 }}>
      {initials}
    </div>
  );
};

// ─── Page principale ───────────────────────────────────────────────
export default function VoamadaChatPage({ onBack }) {
  const [channels, setChannels] = useState([]);
  const [active, setActive] = useState(null); // slug
  const [me, setMe] = useState(null);
  const [authed, setAuthed] = useState(!!getToken());
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [unreadDm, setUnreadDm] = useState(0);
  const isVoamada = typeof window !== "undefined" && /voamada\.com$/i.test(window.location.hostname);

  // Poll les messages privés non lus toutes les 10s
  useEffect(() => {
    if (!authed) return;
    const fetchUnread = () => {
      api.get("/voamada/dm/unread-count").then(({ data }) => setUnreadDm(data.unread || 0)).catch(() => {});
    };
    fetchUnread();
    const id = setInterval(fetchUnread, 10000);
    return () => clearInterval(id);
  }, [authed]);

  // Load channels + me
  useEffect(() => {
    let mounted = true;
    api.get("/voamada/chat/channels").then(({ data }) => {
      if (!mounted) return;
      setChannels(data || []);
      // Activer "general" par défaut
      const def = (data || []).find((c) => c.slug === "general") || data?.[0];
      if (def) setActive(def.slug);
    }).catch(() => {}).finally(() => setLoading(false));
    if (authed) {
      api.get("/voamada/chat/me").then(({ data }) => mounted && setMe(data)).catch(() => setAuthed(false));
    }
    return () => { mounted = false; };
  }, [authed]);

  // Salons favoris (localStorage)
  const [favorites, setFavorites] = useState(() => {
    try { return JSON.parse(localStorage.getItem("voachat-favorites") || "[]"); } catch { return []; }
  });
  const toggleFavorite = (slug) => {
    setFavorites(prev => {
      const next = prev.includes(slug) ? prev.filter(s => s !== slug) : [...prev, slug];
      localStorage.setItem("voachat-favorites", JSON.stringify(next));
      return next;
    });
  };

  // Groupe les salons par kind (avec section "⭐ Favoris" en tête)
  const grouped = useMemo(() => {
    const filter = (c) => !query || c.name.toLowerCase().includes(query.toLowerCase());
    const favs = channels.filter(c => favorites.includes(c.slug) && filter(c));
    return {
      favorites: favs,
      topic:     channels.filter(c => c.kind === "topic" && filter(c)),
      category:  channels.filter(c => c.kind === "category" && filter(c)),
      help:      channels.filter(c => c.kind === "help" && filter(c)),
    };
  }, [channels, query, favorites]);

  // Détecte si on est sur voamada.com
  const backHref = isVoamada ? "/" : "/";

  // Hook notifications navigateur (badge titre + Notification API)
  useDmNotifications(unreadDm, authed);
  const [notifOn, setNotifOn] = useState(false);
  useEffect(() => { setNotifOn(getNotificationsEnabled()); }, []);
  const toggleNotif = async () => {
    const newState = !notifOn;
    const perm = await setNotificationsEnabled(newState);
    setNotifOn(newState && perm === "granted");
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-black/85 backdrop-blur-xl border-b border-yellow-400/20 px-4 md:px-8 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          {/* Bouton retour vers l'accueil Voamada */}
          <Link
            to={backHref}
            className="p-2 -ml-1 rounded-lg hover:bg-yellow-400/15 hover:text-yellow-400 text-white/85 transition shrink-0"
            title="Retour à voamada.com"
            data-testid="chat-back-home"
          >
            <ArrowLeft size={22} />
          </Link>
          <Link to={backHref} className="min-w-0 hover:opacity-90 transition" title="Retour à l'accueil">
            <p className="font-black text-xl md:text-2xl tracking-tight truncate" style={{ fontFamily: "'Anton', sans-serif" }}>
              <span className="text-yellow-400">VOA</span>CHAT 🇲🇬
            </p>
            <p className="text-[10px] uppercase tracking-widest text-white/55 hidden sm:block">← Retour sur voamada.com</p>
          </Link>
        </div>
        {me?.banned && (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-500/20 text-red-300 border border-red-500/40 text-[10px] uppercase tracking-widest font-bold">
            <AlertTriangle size={12} /> Banni
          </span>
        )}
        {authed && me && !me.banned && (
          <div className="flex items-center gap-2">
            <span className="hidden sm:inline text-xs text-white/70 font-bold truncate max-w-[140px]" data-testid="chat-username">
              👤 {me.name || "Membre"}
            </span>
            <Link
              to={isVoamada ? "/messages" : "/messages"}
              className="relative inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-yellow-400/15 hover:bg-yellow-400/30 border border-yellow-400/40 text-yellow-300 text-[10px] uppercase tracking-widest font-bold transition"
              title="Mes messages privés"
              data-testid="chat-dm-link"
            >
              <MessageCircle size={12} /> <span className="hidden sm:inline">Messages</span>
              {unreadDm > 0 && (
                <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] font-black flex items-center justify-center">
                  {unreadDm > 9 ? "9+" : unreadDm}
                </span>
              )}
            </Link>
            <button
              onClick={toggleNotif}
              className={`inline-flex items-center justify-center w-8 h-8 rounded-full border transition ${notifOn ? "bg-yellow-400/20 border-yellow-400/50 text-yellow-300" : "bg-white/5 border-white/20 text-white/60 hover:bg-white/10"}`}
              title={notifOn ? "Notifications ON" : "Activer les notifications navigateur"}
              data-testid="chat-toggle-notif"
            >
              {notifOn ? <Bell size={14} /> : <BellOff size={14} />}
            </button>
            <button
              onClick={() => { clearToken(); window.location.reload(); }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 text-[10px] uppercase tracking-widest font-bold transition"
              title="Se déconnecter"
              data-testid="chat-logout-btn"
            >
              <LogOut size={12} /> <span className="hidden sm:inline">Déconnexion</span>
            </button>
          </div>
        )}
      </header>

      {/* Bandeau de bienvenue (collapsible) */}
      <WelcomeBanner />

      <div className="flex-1 flex flex-col lg:flex-row min-h-0">
        {/* Sidebar salons (desktop uniquement) */}
        <aside className="hidden lg:flex w-72 xl:w-80 shrink-0 flex-col bg-zinc-950 border-r border-white/10">
          <ChannelList
            grouped={grouped}
            active={active}
            onSelect={setActive}
            query={query}
            onQuery={setQuery}
            loading={loading}
            favorites={favorites}
            onToggleFav={toggleFavorite}
          />
        </aside>

        {/* Mobile : sélecteur de salon en tête, pleine largeur */}
        <div className="lg:hidden border-b border-white/10 bg-zinc-950 shrink-0">
          <MobileChannelPicker
            grouped={grouped}
            active={active}
            onSelect={setActive}
            query={query}
            onQuery={setQuery}
            favorites={favorites}
            onToggleFav={toggleFavorite}
          />
        </div>

        {/* Zone messages */}
        <main className="flex-1 flex flex-col min-w-0">
          {!authed ? (
            <NotLoggedIn />
          ) : !active ? (
            <div className="flex-1 flex items-center justify-center text-white/40 p-8 text-center">
              <p>Sélectionne un salon pour commencer à discuter ✨</p>
            </div>
          ) : (
            <ChannelView
              channelSlug={active}
              me={me}
              channels={channels}
            />
          )}
        </main>
      </div>
    </div>
  );
}

// ─── Bannière de bienvenue ─────────────────────────────────────────
const WelcomeBanner = () => {
  const [open, setOpen] = useState(() => !localStorage.getItem("voachat_welcome_dismissed_v1"));
  if (!open) return null;
  return (
    <div className="relative bg-gradient-to-r from-yellow-400/15 via-yellow-400/8 to-transparent border-b border-yellow-400/20 px-4 md:px-8 py-3.5 text-xs md:text-sm">
      <button
        onClick={() => { setOpen(false); localStorage.setItem("voachat_welcome_dismissed_v1", "1"); }}
        className="absolute top-1.5 right-2 p-1 text-white/50 hover:text-white text-xs font-bold"
        aria-label="Fermer"
      >
        ✕
      </button>
      <p className="text-yellow-300 font-bold mb-1">🔥 Bienvenue sur VoaChat 🇲🇬</p>
      <p className="text-white/80 leading-relaxed">
        Discute, partage, rencontre du monde, trouve du travail, du covoiturage, de la musique, des bons plans et entraide entre gasy ❤️ ·
        <span className="text-yellow-400 font-bold"> ✅ Respect</span> ·
        <span className="text-red-400 font-bold"> ❌ Insultes, arnaques, spam</span>
      </p>
    </div>
  );
};

// ─── Sidebar desktop ────────────────────────────────────────────────
const ChannelList = ({ grouped, active, onSelect, query, onQuery, loading, favorites, onToggleFav }) => (
  <>
    <div className="p-3 border-b border-white/10">
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
        <input
          type="search"
          value={query}
          onChange={(e) => onQuery(e.target.value)}
          placeholder="Rechercher un salon…"
          className="w-full bg-white/5 border border-white/10 focus:border-yellow-400/50 outline-none pl-9 pr-3 py-2 text-sm text-white placeholder:text-white/40 rounded"
          data-testid="chat-search"
        />
      </div>
    </div>
    <div className="flex-1 overflow-y-auto py-2">
      {loading ? <p className="p-4 text-xs text-white/40 text-center">Chargement…</p> : (
        <>
          <ChannelGroup label="⭐ Mes favoris" items={grouped.favorites} active={active} onSelect={onSelect} favorites={favorites} onToggleFav={onToggleFav} />
          <ChannelGroup label="Rubriques" items={grouped.topic} active={active} onSelect={onSelect} favorites={favorites} onToggleFav={onToggleFav} />
          <ChannelGroup label="🎭 Catégories de talents" items={grouped.category} active={active} onSelect={onSelect} favorites={favorites} onToggleFav={onToggleFav} />
          <ChannelGroup label="🤝 Entraide & Quotidien" items={grouped.help} active={active} onSelect={onSelect} favorites={favorites} onToggleFav={onToggleFav} />
        </>
      )}
    </div>
  </>
);

const ChannelGroup = ({ label, items, active, onSelect, favorites, onToggleFav }) => {
  if (!items?.length) return null;
  return (
    <div className="px-2 pb-3">
      <p className="px-3 pt-3 pb-1 text-[10px] uppercase tracking-widest font-bold text-white/45">{label}</p>
      <ul className="space-y-0.5">
        {items.map((c) => (
          <li key={c.channel_id} className="group/chan relative">
            <button
              onClick={() => onSelect(c.slug)}
              className={`w-full flex items-center gap-2.5 px-3 py-2 pr-9 rounded text-sm text-left transition ${active === c.slug ? "bg-yellow-400/15 text-yellow-300 font-bold" : "text-white/75 hover:bg-white/5 hover:text-white"}`}
              data-testid={`chat-channel-${c.slug}`}
            >
              <span className="text-base">{c.emoji || "#"}</span>
              <span className="flex-1 truncate">{c.name}</span>
              {c.messages_24h > 0 && <span className="text-[9px] bg-white/10 text-white/60 px-1.5 rounded-full">{c.messages_24h}</span>}
            </button>
            {onToggleFav && (
              <button
                onClick={(e) => { e.stopPropagation(); onToggleFav(c.slug); }}
                className={`absolute right-1.5 top-1/2 -translate-y-1/2 p-1 rounded transition ${favorites?.includes(c.slug) ? "text-yellow-400 opacity-100" : "text-white/30 opacity-0 group-hover/chan:opacity-100 hover:text-yellow-400"}`}
                title={favorites?.includes(c.slug) ? "Retirer des favoris" : "Ajouter aux favoris"}
                data-testid={`chat-fav-${c.slug}`}
              >
                {favorites?.includes(c.slug) ? "★" : "☆"}
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

// ─── Mobile picker (select stylé) ──────────────────────────────────
const MobileChannelPicker = ({ grouped, active, onSelect, query, onQuery, favorites, onToggleFav }) => {
  const [open, setOpen] = useState(false);
  const allChannels = [...grouped.favorites, ...grouped.topic, ...grouped.category, ...grouped.help];
  const current = allChannels.find((c) => c.slug === active);
  return (
    <div className="px-4 py-2.5">
      <button
        onClick={() => setOpen(true)}
        className="w-full flex items-center justify-between gap-2 px-4 py-3 bg-white/5 border border-white/10 rounded text-sm font-bold"
        data-testid="mobile-channel-picker"
      >
        <span className="flex items-center gap-2 truncate">
          <span className="text-lg">{current?.emoji || "💬"}</span>
          <span className="truncate">{current?.name || "Choisir un salon…"}</span>
        </span>
        <span className="text-yellow-400 text-xs">CHANGER ▾</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur flex flex-col" onClick={() => setOpen(false)}>
          <div className="bg-zinc-950 border-b border-yellow-400/30 p-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <p className="font-black text-lg">Choisir un salon</p>
              <button onClick={() => setOpen(false)} className="text-white/60 text-2xl">✕</button>
            </div>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                type="search"
                value={query}
                onChange={(e) => onQuery(e.target.value)}
                placeholder="Rechercher…"
                className="w-full bg-white/5 border border-white/10 outline-none pl-9 pr-3 py-2.5 text-sm rounded"
                autoFocus
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto bg-zinc-950" onClick={(e) => e.stopPropagation()}>
            <ChannelGroup label="⭐ Mes favoris" items={grouped.favorites} active={active} onSelect={(s) => { onSelect(s); setOpen(false); }} favorites={favorites} onToggleFav={onToggleFav} />
            <ChannelGroup label="Rubriques" items={grouped.topic} active={active} onSelect={(s) => { onSelect(s); setOpen(false); }} favorites={favorites} onToggleFav={onToggleFav} />
            <ChannelGroup label="🎭 Catégories de talents" items={grouped.category} active={active} onSelect={(s) => { onSelect(s); setOpen(false); }} favorites={favorites} onToggleFav={onToggleFav} />
            <ChannelGroup label="🤝 Entraide & Quotidien" items={grouped.help} active={active} onSelect={(s) => { onSelect(s); setOpen(false); }} favorites={favorites} onToggleFav={onToggleFav} />
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Pas connecté ──────────────────────────────────────────────────
const NotLoggedIn = () => {
  // Reste sur le domaine courant (ne renvoie PAS vers voamada.com).
  // Une fois loggé, on revient sur /chat automatiquement.
  return (
    <div className="flex-1 flex flex-col items-center justify-center text-center p-8 gap-4">
      <MessagesSquare size={56} className="text-yellow-400/60" />
      <h2 className="font-black text-2xl md:text-3xl tracking-tight" style={{ fontFamily: "'Anton', sans-serif" }}>
        Connecte-toi pour rejoindre la discussion
      </h2>
      <p className="text-white/60 max-w-md text-sm">
        VoaChat est réservé aux membres pour garantir une communauté saine.
        C'est gratuit, ça prend 30 secondes.
      </p>
      <div className="flex flex-wrap items-center justify-center gap-3 mt-2">
        <a
          href="/login?redirect=/chat"
          className="inline-flex items-center gap-2 px-6 py-3 bg-yellow-400 hover:bg-yellow-300 text-black font-black uppercase tracking-widest text-sm transition"
          data-testid="chat-login-btn"
        >
          <LogIn size={14} /> Se connecter
        </a>
        <a
          href="/login?redirect=/chat&tab=signup"
          className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-black uppercase tracking-widest text-sm transition"
          data-testid="chat-signup-btn"
        >
          Créer un compte
        </a>
      </div>
    </div>
  );
};

// ─── Vue d'un salon (messages + input) ─────────────────────────────
const ChannelView = ({ channelSlug, me, channels }) => {
  const [messages, setMessages] = useState([]);
  const [channel, setChannel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [mentionDropdown, setMentionDropdown] = useState(null); // {query, suggestions, position}
  const [slashDropdown, setSlashDropdown] = useState(null);     // {suggestions}
  const scrollRef = useRef(null);
  const lastIdRef = useRef(null);
  const inputRef = useRef(null);
  const isVoamada = typeof window !== "undefined" && /voamada\.com$/i.test(window.location.hostname);

  // Liste de tous les noms d'utilisateurs ayant posté dans ce salon (dédupliqué)
  const knownNames = React.useMemo(() => {
    const set = new Map();
    messages.forEach(m => {
      if (m.user_name && m.user_id !== me?.user_id) {
        const first = m.user_name.split(" ")[0];
        if (first && first.length >= 2) set.set(first.toLowerCase(), first);
      }
    });
    return Array.from(set.values());
  }, [messages, me]);

  // Détecte si l'utilisateur tape "@xxx" ou "/cmd" et propose l'autocomplétion
  const onInputChange = (e) => {
    const v = e.target.value;
    setInput(v);
    const caret = e.target.selectionStart;
    const before = v.slice(0, caret);

    // 1) Mention @user
    const mm = before.match(/@([A-Za-zÀ-ÿ0-9_-]{0,40})$/);
    if (mm) {
      const q = mm[1].toLowerCase();
      const suggestions = knownNames.filter(n => n.toLowerCase().startsWith(q)).slice(0, 5);
      if (suggestions.length > 0) {
        setMentionDropdown({ query: mm[1], suggestions, start: caret - mm[0].length });
        setSlashDropdown(null);
        return;
      }
    }

    // 2) Commande /cmd (uniquement si le message commence par "/")
    if (v.startsWith("/") && !v.includes(" ")) {
      const suggestions = suggestSlashCommands(v.slice(1));
      if (suggestions.length > 0) {
        setSlashDropdown({ suggestions });
        setMentionDropdown(null);
        return;
      }
    }

    setMentionDropdown(null);
    setSlashDropdown(null);
  };

  const applySlash = (cmd) => {
    setInput(`/${cmd.name}${cmd.args ? " " : ""}`);
    setSlashDropdown(null);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const applyMention = (name) => {
    if (!mentionDropdown) return;
    const before = input.slice(0, mentionDropdown.start);
    const afterCaret = input.slice((inputRef.current?.selectionStart) || mentionDropdown.start + mentionDropdown.query.length + 1);
    const newVal = `${before}@${name} ${afterCaret}`;
    setInput(newVal);
    setMentionDropdown(null);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  // Initial load
  useEffect(() => {
    setLoading(true);
    setMessages([]);
    lastIdRef.current = null;
    api.get(`/voamada/chat/messages?channel=${encodeURIComponent(channelSlug)}`)
      .then(({ data }) => {
        setChannel(data.channel);
        setMessages(data.messages || []);
        if (data.messages?.length) lastIdRef.current = data.messages[data.messages.length - 1].message_id;
      })
      .catch((e) => {
        if (e?.response?.status === 401) toast.error("Connecte-toi pour lire les messages");
        else toast.error("Erreur de chargement");
      })
      .finally(() => setLoading(false));
  }, [channelSlug]);

  // Poll for new messages every 4s
  useEffect(() => {
    if (!channelSlug || !channel) return;
    const t = setInterval(async () => {
      if (!lastIdRef.current) return;
      try {
        const { data } = await api.get(`/voamada/chat/messages?channel=${encodeURIComponent(channelSlug)}&after=${lastIdRef.current}`);
        if (data.messages?.length) {
          setMessages((prev) => [...prev, ...data.messages]);
          lastIdRef.current = data.messages[data.messages.length - 1].message_id;
          // Notification spéciale si un de ces messages me mentionne
          const mentions = data.messages.filter(m => m.user_id !== me?.user_id && hasMention(m.body, me?.name));
          if (mentions.length > 0 && typeof window !== "undefined" &&
              "Notification" in window && Notification.permission === "granted" &&
              localStorage.getItem("voachat-notif-enabled") === "1" &&
              (document.hidden || !document.hasFocus())) {
            try {
              const m = mentions[0];
              const n = new Notification(`💬 ${m.user_name} te mentionne dans #${channel?.name || channelSlug}`, {
                body: m.body.slice(0, 140),
                icon: "/icon-192.png",
                tag: "voachat-mention",
                renotify: true,
              });
              n.onclick = () => { window.focus(); n.close(); };
            } catch { /* silent */ }
          }
        }
      } catch { /* silent */ }
    }, 4000);
    return () => clearInterval(t);
  }, [channelSlug, channel, me]);

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async (e) => {
    e?.preventDefault?.();
    let text = input.trim();
    if (!text || sending) return;
    // Transforme les commandes slash (/me, /poll, /8ball, etc.) avant envoi
    const slashResult = processSlashCommand(text, { userName: me?.name });
    if (slashResult !== null) text = slashResult;
    setSending(true);
    try {
      const { data } = await api.post("/voamada/chat/messages", { channel: channelSlug, body: text });
      if (data?.message) {
        setMessages((prev) => [...prev, data.message]);
        lastIdRef.current = data.message.message_id;
      }
      setInput("");
      setSlashDropdown(null);
    } catch (err) {
      toast.error(err?.response?.data?.error || "Impossible d'envoyer");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {channel && (
        <div className="hidden lg:block px-6 py-3 border-b border-white/10 bg-black/50">
          <p className="font-black text-base flex items-center gap-2">
            <span className="text-xl">{channel.emoji || "#"}</span> {channel.name}
          </p>
          {channel.description && <p className="text-xs text-white/55 mt-0.5">{channel.description}</p>}
        </div>
      )}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 md:px-6 py-4 space-y-2.5">
        {loading ? (
          <p className="text-center text-white/40 text-sm py-8">Chargement des messages…</p>
        ) : messages.length === 0 ? (
          <div className="text-center py-10 px-4">
            <p className="text-white/60 text-sm">Sois le premier à écrire dans ce salon ✨</p>
          </div>
        ) : (
          messages.map((m, i) => {
            const prev = messages[i - 1];
            const sameAuthor = prev && prev.user_id === m.user_id && (new Date(m.created_at) - new Date(prev.created_at)) < 5 * 60 * 1000;
            const isMine = me && m.user_id === me.user_id;
            return (
              <MessageBubble
                key={m.message_id}
                msg={m}
                sameAuthor={sameAuthor}
                isMine={isMine}
                currentUserId={me?.user_id}
                currentUserName={me?.name}
                isVoamada={isVoamada}
              />
            );
          })
        )}
      </div>

      <form onSubmit={send} className="border-t border-white/10 p-3 md:p-4 bg-zinc-950">
        {me?.banned ? (
          <p className="text-center text-red-400 text-sm">Tu es banni du chat. Contacte un administrateur.</p>
        ) : (
          <div className="relative flex items-end gap-2">
            <textarea
              ref={inputRef}
              value={input}
              onChange={onInputChange}
              onKeyDown={(e) => {
                if (mentionDropdown && (e.key === "Tab" || (e.key === "Enter" && !e.shiftKey))) {
                  e.preventDefault();
                  applyMention(mentionDropdown.suggestions[0]);
                  return;
                }
                if (slashDropdown && (e.key === "Tab" || (e.key === "Enter" && !e.shiftKey))) {
                  e.preventDefault();
                  applySlash(slashDropdown.suggestions[0]);
                  return;
                }
                if ((mentionDropdown || slashDropdown) && e.key === "Escape") {
                  e.preventDefault(); setMentionDropdown(null); setSlashDropdown(null); return;
                }
                if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
              }}
              placeholder={`Écrire dans #${channel?.name || channelSlug}…  (@ pour mentionner, / pour commande)`}
              rows={1}
              maxLength={2000}
              className="flex-1 resize-none bg-white/5 border border-white/10 focus:border-yellow-400/60 outline-none px-3 py-2.5 text-sm rounded leading-snug"
              data-testid="chat-input"
            />
            {mentionDropdown && mentionDropdown.suggestions.length > 0 && (
              <div className="absolute bottom-full left-0 mb-1 bg-zinc-900 border border-yellow-400/30 rounded-xl shadow-xl py-1 z-30 min-w-[180px]" data-testid="mention-dropdown">
                {mentionDropdown.suggestions.map((name, i) => (
                  <button
                    key={name}
                    type="button"
                    onClick={() => applyMention(name)}
                    className={`w-full text-left px-3 py-1.5 text-sm hover:bg-yellow-400/15 ${i === 0 ? "bg-white/[0.04] text-yellow-300" : "text-white"}`}
                    data-testid={`mention-suggestion-${name}`}
                  >
                    <span className="text-yellow-400">@</span>{name}
                  </button>
                ))}
              </div>
            )}
            {slashDropdown && slashDropdown.suggestions.length > 0 && (
              <div className="absolute bottom-full left-0 mb-1 bg-zinc-900 border border-cyan-400/30 rounded-xl shadow-xl py-1 z-30 min-w-[280px] max-w-[400px]" data-testid="slash-dropdown">
                <div className="px-3 py-1 text-[9px] uppercase tracking-widest text-cyan-300/60 border-b border-white/10 mb-1">Commandes — Tab pour appliquer</div>
                {slashDropdown.suggestions.map((cmd, i) => (
                  <button
                    key={cmd.name}
                    type="button"
                    onClick={() => applySlash(cmd)}
                    className={`w-full text-left px-3 py-1.5 text-sm hover:bg-cyan-400/15 ${i === 0 ? "bg-white/[0.04]" : ""}`}
                    data-testid={`slash-suggestion-${cmd.name}`}
                  >
                    <span className="text-cyan-300 font-mono">/{cmd.name}</span>
                    {cmd.args && <span className="text-white/40 ml-1">{cmd.args}</span>}
                    <div className="text-[10px] text-white/50">{cmd.desc}</div>
                  </button>
                ))}
              </div>
            )}
            <button
              type="submit"
              disabled={!input.trim() || sending}
              className="shrink-0 px-4 py-2.5 bg-yellow-400 hover:bg-yellow-300 disabled:opacity-50 text-black rounded inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-widest transition"
              data-testid="chat-send"
            >
              <Send size={13} /> Envoyer
            </button>
          </div>
        )}
        <p className="text-[10px] text-white/40 mt-1.5">Entrée pour envoyer · Shift+Entrée saut de ligne · <span className="text-yellow-300">@</span>nom mention · <span className="text-cyan-300">/</span>help commandes · respect ❤️</p>
      </form>
    </>
  );
};

const MessageBubble = ({ msg, sameAuthor, isMine, currentUserId, currentUserName, isVoamada }) => {
  const canDm = !isMine && msg.user_id && msg.user_id !== currentUserId;
  const dmHref = (isVoamada ? "/messages" : "/messages") + "?with=" + encodeURIComponent(msg.user_id || "");
  const mentionsMe = !isMine && hasMention(msg.body, currentUserName);
  return (
    <div className={`flex gap-2.5 ${isMine ? "flex-row-reverse" : ""}`} data-testid={`chat-msg-${msg.message_id}`}>
      {!sameAuthor ? (
        canDm ? (
          <Link to={dmHref} title={`Envoyer un message privé à ${msg.user_name}`} className="hover:opacity-80 transition">
            <Avatar name={msg.user_name} url={msg.avatar_url} size={32} />
          </Link>
        ) : (
          <Avatar name={msg.user_name} url={msg.avatar_url} size={32} />
        )
      ) : (
        <div className="w-8 shrink-0" />
      )}
      <div className={`max-w-[78%] ${isMine ? "items-end" : "items-start"} flex flex-col`}>
        {!sameAuthor && (
          <p className="text-[11px] mb-0.5 flex items-center gap-2 flex-wrap">
            {canDm ? (
              <Link
                to={dmHref}
                className={`font-bold hover:underline ${isMine ? "text-yellow-300" : "text-yellow-400"}`}
                data-testid={`open-dm-${msg.user_id}`}
              >
                {msg.user_name || "Anonyme"}
              </Link>
            ) : (
              <span className={`font-bold ${isMine ? "text-yellow-300" : "text-yellow-400"}`}>{msg.user_name || "Anonyme"}</span>
            )}
            <UserBadges badges={msg.badges} />
            <span className="text-white/40">{fmtTime(msg.created_at)}</span>
            {canDm && (
              <Link to={dmHref} className="opacity-0 group-hover:opacity-100 hover:opacity-100 text-[10px] text-white/45 hover:text-yellow-400 transition" title="Message privé">
                💬 MP
              </Link>
            )}
          </p>
        )}
        <div className={`group px-3.5 py-2 text-sm leading-relaxed whitespace-pre-wrap break-words rounded-2xl ${
          mentionsMe ? "bg-yellow-400/15 border border-yellow-400/50 text-yellow-100" :
          isMine ? "bg-yellow-400 text-black rounded-br-sm" : "bg-white/[0.07] text-white rounded-bl-sm"
        }`}>
          {renderRich(msg.body, currentUserName)}
        </div>
      </div>
    </div>
  );
};

// ─── Rich text : URLs + mentions @username ─────────────────────────
const URL_RE = /(https?:\/\/[^\s]+)/g;
const MENTION_RE = /@([A-Za-zÀ-ÿ0-9_-]{2,40})/g;

function renderRich(text, myName) {
  if (!text) return null;
  // Découpe en tokens : URL, mention, ou texte brut
  const tokens = [];
  let lastIndex = 0;
  const combined = /(https?:\/\/[^\s]+)|@([A-Za-zÀ-ÿ0-9_-]{2,40})/g;
  let m;
  while ((m = combined.exec(text)) !== null) {
    if (m.index > lastIndex) tokens.push({ type: "text", value: text.slice(lastIndex, m.index) });
    if (m[1]) tokens.push({ type: "url", value: m[1] });
    else if (m[2]) tokens.push({ type: "mention", value: m[2], full: m[0] });
    lastIndex = combined.lastIndex;
  }
  if (lastIndex < text.length) tokens.push({ type: "text", value: text.slice(lastIndex) });

  return tokens.map((t, i) => {
    if (t.type === "url") {
      return <a key={i} href={t.value} target="_blank" rel="noreferrer" className="underline hover:text-yellow-300 break-all">{t.value}</a>;
    }
    if (t.type === "mention") {
      const isMe = myName && t.value.toLowerCase() === myName.toLowerCase().split(" ")[0].toLowerCase();
      return (
        <span key={i} className={`font-bold ${isMe ? "bg-yellow-400/30 text-yellow-200 px-1 rounded" : "text-cyan-300 hover:text-cyan-200"}`}>
          {t.full}
        </span>
      );
    }
    return <React.Fragment key={i}>{t.value}</React.Fragment>;
  });
}

// Détecte si un message mentionne `myName` (1er prénom, insensible à la casse)
function hasMention(body, myName) {
  if (!body || !myName) return false;
  const first = myName.toLowerCase().split(" ")[0];
  MENTION_RE.lastIndex = 0;
  let m;
  while ((m = MENTION_RE.exec(body)) !== null) {
    if (m[1].toLowerCase() === first) return true;
  }
  return false;
}

// ─── Badges Fan TopMada (affichés à côté du pseudo) ────────────────
const UserBadges = ({ badges }) => {
  if (!Array.isArray(badges) || badges.length === 0) return null;
  return (
    <span className="inline-flex items-center gap-1" data-testid="chat-user-badges">
      {badges.slice(0, 3).map((b) => (
        <span
          key={b.key}
          title={`${b.label} — Badge Fan TopMada`}
          className="inline-flex items-center text-[10px] leading-none px-1.5 py-0.5 rounded-full bg-amber-400/15 border border-amber-400/35 text-amber-200 font-bold"
          data-testid={`badge-${b.key}`}
        >
          <span className="text-[12px] mr-0.5">{b.emoji}</span>
          <span className="hidden sm:inline">{b.label}</span>
        </span>
      ))}
    </span>
  );
};
